import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  ShieldCheck,
  ShieldX,
  AlertTriangle,
  RefreshCcw,
  Search,
  Globe,
  Server,
} from "lucide-react";

// Fix leaflet default icon (Webpack issue — point to assets via CDN)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const RESULT_BADGE = {
  success: { label: "✅ OK", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  wrong_password: { label: "🔒 Pwd", cls: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  not_found: { label: "🔍 NF", cls: "bg-zinc-700/40 text-zinc-400 border-zinc-700" },
  banned: { label: "🚫 BAN", cls: "bg-red-500/10 text-red-400 border-red-500/30" },
};

function Stat({ icon: Icon, label, value, color = "text-zinc-200" }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-sm p-4 flex items-start gap-3">
      <Icon size={18} className={color + " mt-0.5"} />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{label}</div>
        <div className={"text-xl font-bold " + color}>{value}</div>
      </div>
    </div>
  );
}

export default function Activity() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [limit, setLimit] = useState(200);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/activity/recent?limit=${limit}`);
      setRows(data.items || []);
    } catch (e) {
      console.error("activity load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [limit]);

  // Stats
  const stats = useMemo(() => {
    const success = rows.filter(r => r.result === "success");
    const failed = rows.filter(r => r.result !== "success");
    const ips = new Set(rows.map(r => r.ip).filter(Boolean));
    const countries = new Set(rows.map(r => r.country).filter(Boolean));
    const cities = new Set(rows.map(r => r.city).filter(Boolean));
    const proxies = rows.filter(r => r.proxy).length;
    return {
      total: rows.length,
      success: success.length,
      failed: failed.length,
      uniqueIps: ips.size,
      uniqueCountries: countries.size,
      uniqueCities: cities.size,
      proxies,
    };
  }, [rows]);

  // Filtered rows
  const filtered = useMemo(() => {
    if (!filter.trim()) return rows;
    const q = filter.trim().toLowerCase();
    return rows.filter(r =>
      (r.email || "").toLowerCase().includes(q) ||
      (r.ip || "").includes(q) ||
      (r.country || "").toLowerCase().includes(q) ||
      (r.city || "").toLowerCase().includes(q) ||
      (r.isp || "").toLowerCase().includes(q)
    );
  }, [rows, filter]);

  // Pins for map (only rows with lat/lon)
  const pins = useMemo(() => {
    const buckets = new Map(); // "lat,lon" -> { lat, lon, city, country, count, last }
    for (const r of filtered) {
      const lat = r.geo?.lat ?? r.lat;
      const lon = r.geo?.lon ?? r.lon;
      if (typeof lat !== "number" || typeof lon !== "number") continue;
      const key = `${lat.toFixed(2)},${lon.toFixed(2)}`;
      const existing = buckets.get(key);
      if (existing) {
        existing.count += 1;
        if (r.at > existing.last) existing.last = r.at;
        existing.rows.push(r);
      } else {
        buckets.set(key, {
          lat, lon,
          city: r.city || "?",
          country: r.country || "?",
          isp: r.isp || "?",
          count: 1,
          last: r.at,
          rows: [r],
        });
      }
    }
    return [...buckets.values()];
  }, [filtered]);

  return (
    <div className="space-y-6" data-testid="activity-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black tracking-tight flex items-center gap-2">
            <MapPin size={22} className="text-[#2AABEE]" /> Actividad · IPs · Geolocalización
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Logins recientes con IP, país, ciudad e ISP — vía ip-api.com (~5 km accuracy).
          </p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          size="sm"
          className="border-zinc-800 hover:border-[#2AABEE] rounded-sm"
          data-testid="activity-refresh-btn"
        >
          <RefreshCcw size={14} className="mr-1.5" />
          Refrescar
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Stat icon={Globe}        label="Total"        value={stats.total}     />
        <Stat icon={ShieldCheck}  label="Éxitos"       value={stats.success}   color="text-emerald-400" />
        <Stat icon={ShieldX}      label="Fallidos"     value={stats.failed}    color="text-amber-400" />
        <Stat icon={Server}       label="IPs únicas"   value={stats.uniqueIps} />
        <Stat icon={Globe}        label="Países"       value={stats.uniqueCountries} />
        <Stat icon={MapPin}       label="Ciudades"     value={stats.uniqueCities} />
        <Stat icon={AlertTriangle} label="Proxy/VPN"   value={stats.proxies}   color="text-red-400" />
      </div>

      {/* Map */}
      <Card className="bg-zinc-950 border-zinc-900 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-900 flex items-center justify-between">
          <div className="font-bold text-sm flex items-center gap-2">
            <MapPin size={14} className="text-[#2AABEE]" />
            Mapa de logins — {pins.length} ubicaciones
          </div>
          <div className="text-[10px] text-zinc-500 font-mono">© OpenStreetMap</div>
        </div>
        <div style={{ height: 380 }} data-testid="activity-map">
          <MapContainer
            center={[20.0, -100.0]}
            zoom={3}
            style={{ height: "100%", width: "100%", background: "#0a0a0a" }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {pins.map((p) => (
              <CircleMarker
                key={`pin-${p.lat.toFixed(3)}-${p.lon.toFixed(3)}`}
                center={[p.lat, p.lon]}
                radius={6 + Math.min(p.count, 10)}
                pathOptions={{
                  color: "#2AABEE",
                  fillColor: "#2AABEE",
                  fillOpacity: 0.55,
                  weight: 2,
                }}
              >
                <Popup>
                  <div className="text-xs">
                    <div className="font-bold mb-1">{p.city}, {p.country}</div>
                    <div className="text-zinc-600">ISP: {p.isp}</div>
                    <div className="text-zinc-600">Logins: <b>{p.count}</b></div>
                    <div className="text-zinc-600">Último: {String(p.last).slice(0, 19)}</div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      </Card>

      {/* Filter */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            placeholder="Filtrar por email, IP, país, ciudad o ISP..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pl-9 bg-zinc-950 border-zinc-900 focus-visible:ring-[#2AABEE] rounded-sm"
            data-testid="activity-filter-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500 font-mono">limit:</span>
          {[50, 100, 200, 500].map(n => (
            <button
              key={n}
              onClick={() => setLimit(n)}
              data-testid={`activity-limit-${n}`}
              className={
                "text-xs font-mono px-2 py-1 rounded-sm border transition " +
                (limit === n
                  ? "border-[#2AABEE] text-[#2AABEE] bg-[#2AABEE]/10"
                  : "border-zinc-800 text-zinc-500 hover:border-zinc-700")
              }
            >{n}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card className="bg-zinc-950 border-zinc-900 rounded-sm overflow-x-auto">
        {loading && rows.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 font-mono text-sm">Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 font-mono text-sm">
            Sin actividad registrada todavía.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-950 border-b border-zinc-900">
              <tr className="text-xs text-zinc-500 font-mono uppercase">
                <th className="px-4 py-3 text-left">Cuando</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">IP</th>
                <th className="px-4 py-3 text-left">Ubicación</th>
                <th className="px-4 py-3 text-left">ISP</th>
                <th className="px-4 py-3 text-left">Dispositivo</th>
                <th className="px-4 py-3 text-left">Flags</th>
                <th className="px-4 py-3 text-left">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const badge = RESULT_BADGE[r.result] || RESULT_BADGE.not_found;
                const rowKey = `login-${r.at || i}-${r.ip || "noip"}-${r.email || "anon"}`;
                return (
                  <tr key={rowKey} className="border-b border-zinc-900 hover:bg-zinc-900/40" data-testid={`activity-row-${i}`}>
                    <td className="px-4 py-3 font-mono text-[11px] text-zinc-400 whitespace-nowrap">
                      {String(r.at || "").slice(0, 19).replace("T", " ")}
                    </td>
                    <td className="px-4 py-3 text-zinc-300 max-w-[180px] truncate" title={r.email}>{r.email || "—"}</td>
                    <td className="px-4 py-3 font-mono text-[12px] text-zinc-400">{r.ip || "—"}</td>
                    <td className="px-4 py-3 text-zinc-300">
                      {r.city || r.country ? (
                        <span>{r.city || "?"}, <span className="text-zinc-500">{r.country}</span></span>
                      ) : <span className="text-zinc-700">—</span>}
                    </td>
                    <td className="px-4 py-3 text-zinc-500 text-xs max-w-[200px] truncate" title={r.isp}>{r.isp || "—"}</td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">{r.ua_short || "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      {r.proxy ? <span className="px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 mr-1">vpn</span> : null}
                      {r.hosting ? <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400">dc</span> : null}
                    </td>
                    <td className="px-4 py-3">
                      <span className={"px-2 py-0.5 rounded-sm border text-[10px] font-mono " + badge.cls}>
                        {badge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <p className="text-[11px] text-zinc-600 font-mono text-center">
        Tip: el push automático "🛡️ Nuevo login desde X" se manda cuando una IP nunca vista antes inicia sesión.
      </p>
    </div>
  );
}
